import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { BackTop, useScrollTop, Breadcrumb, SectionHeading } from "@/components/feature/UI";
import { channels } from "@/mocks/categories";
import { news } from "@/mocks/news";
import { siteName, siteSlogan, useLdJson, useSeo } from "@/hooks/useSeo";

const values = [
  {
    icon: "ri-focus-3-line",
    title: "对事，不对人",
    desc: "我们拆解的是行业现象与人设逻辑，而非针对某个具体的个人。用词克制，边界清晰，绝不做情绪的放大器。",
  },
  {
    icon: "ri-check-double-line",
    title: "宁慢，不抢发",
    desc: "真相需要时间沉淀。对缺乏证据或存在争议的消息，我们宁可放慢更新，也要在标题与正文中如实标注「待核实」。",
  },
  {
    icon: "ri-shield-check-line",
    title: "保护，不消费",
    desc: "我们拒绝传播偷拍、私密与可识别个人隐私的内容，涉及重大法律或名誉风险时，会建议通过正规渠道处理。",
  },
  {
    icon: "ri-eye-2-line",
    title: "理性，不煽动",
    desc: "鼓励理性讨论，反对网暴与拉踩。我们关心事件，更关心事件折射出的行业问题与每一位读者的判断力。",
  },
];

const principles = [
  "每一篇盘点都尽量标注可查证的时间线与来源",
  "涉及争议内容明确标注「待核实」字样",
  "人物采用化名或隐去可识别信息处理",
  "收到异议后认真复核，确有偏差将及时勘误",
  "不发布虚假信息、诽谤或侵权内容",
  "匿名投稿与隐私信息全程保护",
];

const testimonials = [
  {
    name: "一位追星十年的老粉",
    text: "这里的角度不一样，不跟风骂，也不无脑吹，就是很冷静地告诉你运营和人设是怎么回事。看了几篇，反而追得更清醒了。",
  },
  {
    name: "某MCN在职编导",
    text: "有些描述一看就是内行写的，连投放节奏都写得挺准。当作行业观察来看，比很多泛泛而谈的营销号扎实。",
  },
  {
    name: "普通吃瓜网友",
    text: "「待核实」三个字很戳我。现在愿意把不确定说成不确定的号不多了，这种态度值得尊重。",
  },
];

export default function AboutPage() {
  useScrollTop();
  useSeo({
    title: `关于我们 - ${siteName} | 理性吃瓜，看见行业`,
    description: `了解${siteName}：一档聚焦新晋网红、小艺人、练习生与新入行演员幕后现象的内容站。坚持理性吃瓜、对事不对人，专注娱乐工业观察。`,
    keywords: "每日新瓜,关于我们,理性吃瓜,娱乐观察,内容原则",
  });

  useLdJson(
    [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `关于${siteName}`,
        description: "每日新瓜网的定位、理念与内容原则介绍。",
        mainEntity: {
          "@type": "Organization",
          name: siteName,
          slogan: siteSlogan,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: window.location.origin + "/" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
    ],
    "about-ld"
  );

  return (
    <>
      <Header />
      <main>
        <section className="border-b border-background-200 bg-background-100/60">
          <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <span className="font-label text-xs font-semibold uppercase tracking-[0.3em] text-primary-600">
                  ABOUT US · 关于本站
                </span>
                <h1 className="font-display mt-3 text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                  新瓜每天都有，
                  <br className="hidden md:block" />
                  但值得被<em className="not-italic text-primary-600">认真对待</em>
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
                  {siteName}是一档关注娱乐工业与人设现象的内容站。我们聚焦新晋网红、小艺人、练习生与新入行演员的幕后故事，记录那些被算法放大的偶然，也拆解那些被精心设计的必然。
                </p>
              </div>
              <div className="grid shrink-0 grid-cols-3 gap-3">
                {[
                  { v: `${channels.length}`, l: "内容栏目" },
                  { v: `${news.length}+`, l: "深度文章" },
                  { v: "365", l: "全年更新" },
                ].map((s) => (
                  <div key={s.l} className="rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-center">
                    <p className="font-display text-2xl font-black text-primary-600">{s.v}</p>
                    <p className="text-xs text-foreground-500">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1200px] px-4 py-12 md:px-6">
          <SectionHeading title="我们如何看待「吃瓜」" en="OUR STANCE" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="font-display mt-4 text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-primary-600">
          <div className="mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-8 px-4 py-12 md:grid-cols-2 md:px-6">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-background-50/15 px-3 py-1 text-xs text-background-50">
                <i className="ri-clipboard-line"></i>
                内容原则
              </span>
              <h2 className="font-display mt-3 text-2xl font-black leading-snug text-background-50 md:text-3xl">
                我们写什么，也写着怎么不写
              </h2>
              <p className="mt-3 text-sm leading-7 text-background-100/85 md:text-base">
                在信息爆炸的时代，真正的勇气不是抢在所有人前面，而是敢在混乱中说一句「这事还没定论」。这些是我们给自己划下的底线。
              </p>
            </div>
            <ul className="grid gap-3">
              {principles.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-md bg-background-50/10 px-4 py-3 text-sm text-background-50"
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                    <i className="ri-check-line text-xs"></i>
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="bg-background-100/70">
          <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6">
            <SectionHeading title="读者怎么看我们" en="FROM READERS" />
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="flex flex-col rounded-lg border border-background-200/80 bg-background-50 p-5">
                  <span className="font-display text-4xl leading-none text-primary-300">“</span>
                  <blockquote className="mt-2 flex-1 text-sm leading-7 text-foreground-700">{t.text}</blockquote>
                  <figcaption className="mt-4 flex items-center gap-2 border-t border-background-200 pt-3 text-sm text-foreground-500">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                      <i className="ri-user-heart-line"></i>
                    </span>
                    {t.name}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1200px] px-4 py-12 md:px-6">
          <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-2 md:p-8">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                <i className="ri-hand-heart-line"></i>
                一起参与
              </span>
              <h2 className="font-display mt-3 text-2xl font-black leading-snug text-foreground-950">
                想加入我们的观察？
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                如果你有值得挖掘的线索，或对行业有自己的观察，欢迎投稿与编辑部交流。我们始终欢迎认真、理性且有温度的声音。
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-send-plane-line"></i>
                  投稿爆料
                </Link>
                <Link to="/faq" className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300">
                  查看常见问题
                </Link>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <p className="text-sm text-foreground-500">与编辑部保持联系：</p>
              {[
                { i: "ri-mail-line", t: "投稿与授权", v: "dailygossip@example.com" },
                { i: "ri-wechat-line", t: "官方公众号", v: "每日新瓜" },
                { i: "ri-time-line", t: "更新节奏", v: "每日热点 · 栏目定期盘点" },
              ].map((c) => (
                <div key={c.v} className="flex items-center gap-3 rounded-md border border-background-200 bg-background-50 px-4 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className={c.i}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">{c.t}</p>
                    <p className="text-sm font-medium text-foreground-900">{c.v}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackTop />
    </>
  );
}